import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect, useState } from 'react';

import { Box, Grid, Loader } from '~/components/blocks';
import { ReceptionDrawer } from '~/components/layouts/ReceptionDrawer';
import { ReceptionDrawerFileMenu } from '~/components/layouts/ReceptionDrawer/FileMenu';
import { NotificationConfirmModal } from '~/components/pages/Reception/LineVisitorProfileDrawer/NotificationConfirmModal';
import { NotificationCongestionModal } from '~/components/pages/Reception/LineVisitorProfileDrawer/NotificationCongestionModal';
import { SendUnavailableModal } from '~/components/pages/Reception/LineVisitorProfileDrawer/SendUnavailableModal';
import { useActivateLineMessageQuota } from '~/components/pages/Reception/LineVisitorProfileDrawer/use-activate-line-message-quota';
import { LineVisitorChargeModal } from '~/components/partials/LineVisitorChargeModal';
import { LineVisitorKarte } from '~/components/partials/LineVisitorKarte';
import { useMarkNotification } from '~/components/partials/PatientProfileDrawer/use-mark-notification';
import { FileMenuButton } from '~/components/partials/ReceptionDrawer/FileMenuButton';
import { ReceptionMainBox } from '~/components/partials/ReceptionDrawer/ReceptionMainBox';
import { WithPrescriptionIdentifyStatusIcon } from '~/components/partials/ReceptionDrawer/WithPrescriptionIdentifyStatusIcon';
import { VisitorProfile } from '~/components/partials/VisitorProfile';
import { ReceptionFileType } from '~/constants/reception_file_type';
import { useSwapDrawer } from '~/hooks/use-swap-drawer';
import { lineVisitorProfileDrawerState } from '~/state/reception/atoms';
import { LineVisitorProfileDrawerState as LineVisitorProfileDrawerStateType } from '~/state/reception/types';
import { lineVisitorFileAreaState } from '~/state/reception_drawer/atoms';
import { ReceptionDrawerFileType } from '~/state/reception_drawer/types';

import { FileArea } from './FileArea';
import { Footer } from './Footer';
import { useFetchAppointment } from './use-fetch-appointment';

const ProfileBox = styled(Box)(({ theme }) =>
  css({
    overflow: 'auto',
    padding: `${theme.space.l} ${theme.space.l} ${theme.space.m}`,
    [`@media ${theme.mediaQueries.tablet}`]: {
      padding: `${theme.space.l} ${theme.space.l} ${theme.space.xs}`,
    },
  }),
);

export const LineVisitorProfileDrawer = () => {
  const theme = useTheme();
  const {
    state: { isOpen, appointmentId },
    handleTransitionEnd,
  } = useSwapDrawer<LineVisitorProfileDrawerStateType>(
    lineVisitorProfileDrawerState,
    'appointmentId',
  );

  const [isFileAreaOpened, setOpenFileArea] = useState(false);
  const setFileAreaState = useSetAtom(lineVisitorFileAreaState);
  const resetLineVisitorProfileDrawerState = useResetAtom(lineVisitorProfileDrawerState);

  const { loading, appointment } = useFetchAppointment(isOpen, appointmentId);

  const handleOpenFileArea = useCallback(
    (type: ReceptionDrawerFileType) => {
      setOpenFileArea(true);
      setFileAreaState((state) => ({ ...state, type, appointmentId }));
    },
    [appointmentId, setFileAreaState],
  );

  const handleCloseFileArea = useCallback(() => {
    setOpenFileArea(false);
  }, []);

  const handleClose = useCallback(() => {
    resetLineVisitorProfileDrawerState();
  }, [resetLineVisitorProfileDrawerState]);

  useEffect(() => {
    setOpenFileArea(false);
  }, [appointmentId, isOpen]);

  useMarkNotification(appointmentId);

  const { activateLineMessageQuota, activateLoading, called } = useActivateLineMessageQuota();
  const line_visitor = appointment?.lineVisitor;
  const messageSendable =
    line_visitor?.__typename === 'LineVisitor' ? line_visitor.messageSendable : false;

  useEffect(() => {
    if (line_visitor?.id && !messageSendable && !activateLoading && !called) {
      const activateData = async () => {
        await activateLineMessageQuota(line_visitor.id);
      };
      activateData();
    }
  }, [line_visitor?.id, messageSendable, activateLineMessageQuota, activateLoading, called]);

  if (activateLoading) {
    return (
      <ReceptionDrawer
        isOpen={isOpen}
        onClose={handleClose}
        onTransitionEnd={handleTransitionEnd}
        isExpended={false}
      >
        <ReceptionMainBox>
          <Loader inside open />
        </ReceptionMainBox>
      </ReceptionDrawer>
    );
  }

  return (
    <>
      <ReceptionDrawer
        isOpen={isOpen}
        onClose={handleClose}
        onTransitionEnd={handleTransitionEnd}
        isExpended={isFileAreaOpened}
      >
        <ReceptionDrawerFileMenu>
          {appointment?.yakumaruPrescriptionIdentificationResult &&
          appointment?.status === 'booked' ? (
            <WithPrescriptionIdentifyStatusIcon
              status={appointment.yakumaruPrescriptionIdentificationResult.status}
            >
              <FileMenuButton
                fileName="処方箋"
                active={true}
                statusLabel="データあり"
                onClick={() => handleOpenFileArea(ReceptionFileType.prescription)}
                marginTop={theme.space.xl}
              />
            </WithPrescriptionIdentifyStatusIcon>
          ) : (
            <FileMenuButton
              fileName="処方箋"
              active={true}
              statusLabel="データあり"
              onClick={() => handleOpenFileArea(ReceptionFileType.prescription)}
              marginTop={theme.space.xl}
            />
          )}
          <FileMenuButton fileName="保険情報" active={false} statusLabel="データなし" />
          <FileMenuButton
            fileName="問診票"
            active={true}
            statusLabel="データあり"
            onClick={() => handleOpenFileArea(ReceptionFileType.questionnaire)}
          />
          <FileMenuButton fileName="お薬手帳" active={false} statusLabel="データなし" />
        </ReceptionDrawerFileMenu>
        {isFileAreaOpened && <FileArea onClose={handleCloseFileArea} />}
        <ReceptionMainBox>
          <Grid height="100%" gridTemplateRows="min-content 1fr min-content" overflow="hidden">
            <ProfileBox>
              <VisitorProfile loading={loading} visitor={appointment?.lineVisitor} />
            </ProfileBox>
            {appointment ? (
              <>
                <Box overflow="auto">
                  <LineVisitorKarte appointment={appointment} />
                </Box>
                {appointment.lineVisitor && <Footer appointment={appointment} />}
              </>
            ) : (
              <Loader inside open />
            )}
          </Grid>
        </ReceptionMainBox>
      </ReceptionDrawer>
      <NotificationCongestionModal />
      <NotificationConfirmModal />
      <SendUnavailableModal />
      <LineVisitorChargeModal />
    </>
  );
};
